import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { sanityFetch } from "../sanityClient";

interface HandbookDocument {
  icon?: string;
  title: string;
  description?: string;
  url?: string;
  badge?: string;
  accentColor?: string;
}

interface VendorHandbook {
  introText?: string;
  documents?: HandbookDocument[];
}

const FALLBACK_DOCS: HandbookDocument[] = [
  {
    icon: "📋",
    title: "Policy Manual",
    description:
      "The full SJFM Policy Manual. A living document — policies may change from time to time as needed.",
    badge: "Living Document",
    accentColor: "#2d6a14",
  },
  {
    icon: "⚖️",
    title: "SJFM By-Laws",
    description:
      "Governance and organizational structure of the St. John's Farmers' Market Cooperative.",
    accentColor: "#1e4a8a",
  },
  {
    icon: "📘",
    title: "Vendor Handbook",
    description:
      "Selection criteria, market policies, booth procedures, and operational details for vendors.",
    badge: "Required Reading",
    accentColor: "#c6252d",
  },
  {
    icon: "🍳",
    title: "Kitchen Handbook",
    description:
      "Guidelines for vendors and individuals who use the SJFM on-site licensed commercial kitchen.",
    accentColor: "#C19A3B",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const Policies: React.FC = () => {
  const [handbook, setHandbook] = useState<VendorHandbook | null>(null);

  useEffect(() => {
    sanityFetch<VendorHandbook>(
      `*[_type == "vendorHandbook"][0]{introText, "documents": documents[]{icon, title, description, badge, accentColor, "url": file.asset->url}}`
    ).then((data) => { if (data) setHandbook(data); });
  }, []);

  const introText =
    handbook?.introText ??
    "The St. John's Farmers' Market Cooperative maintains several policy documents that outline the rules and procedures for markets and for all co-operative activities.";
  const docs = handbook?.documents?.length ? handbook.documents : FALLBACK_DOCS;

  return (
    <div>
      {/* HERO */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(193,154,59,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 860, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.span
              variants={fadeUp}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(193,154,59,0.14)", border: "1px solid rgba(193,154,59,0.35)", borderRadius: 999, padding: "0.35rem 1rem", color: "#C19A3B", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1.25rem" }}
            >
              <span style={{ width: 7, height: 7, background: "#C19A3B", borderRadius: "50%", display: "inline-block" }} />
              Official Documents
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}
            >
              Market{" "}
              <span style={{ background: "linear-gradient(90deg, #C19A3B, #e8c060)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Policies
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.75, maxWidth: 640 }}
            >
              {introText}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* DOCUMENTS GRID */}
      <section style={{ background: "#fffdf8", padding: "4.5rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: "1.75rem" }}
          >
            {docs.map((doc, i) => {
              const accent = doc.accentColor ?? "#2d6a14";
              const hasUrl = Boolean(doc.url);

              const cardContent = (
                <div
                  style={{
                    background: hasUrl ? "#ffffff" : "#f9f9f9",
                    border: `1px solid ${accent}20`,
                    borderTop: `5px solid ${hasUrl ? accent : "#d0d0d0"}`,
                    borderRadius: 18,
                    padding: "2rem 1.75rem 1.75rem",
                    height: "100%",
                    boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
                    display: "flex",
                    flexDirection: "column",
                    opacity: hasUrl ? 1 : 0.65,
                    transition: "transform 0.22s ease, box-shadow 0.22s ease",
                    cursor: hasUrl ? "pointer" : "default",
                  }}
                  onMouseEnter={(e) => {
                    if (!hasUrl) return;
                    const el = e.currentTarget as HTMLDivElement;
                    el.style.transform = "translateY(-4px)";
                    el.style.boxShadow = `0 12px 36px ${accent}28`;
                  }}
                  onMouseLeave={(e) => {
                    if (!hasUrl) return;
                    const el = e.currentTarget as HTMLDivElement;
                    el.style.transform = "translateY(0)";
                    el.style.boxShadow = "0 2px 20px rgba(0,0,0,0.055)";
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      background: `${accent}14`,
                      border: `1px solid ${accent}22`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.8rem",
                      marginBottom: "1.25rem",
                      flexShrink: 0,
                    }}
                  >
                    {doc.icon ?? "📄"}
                  </div>

                  {/* Title + Badge */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.65rem" }}>
                    <h3 style={{ color: "#0d1b2a", fontWeight: 800, fontSize: "1.15rem", margin: 0, lineHeight: 1.3 }}>
                      {doc.title}
                    </h3>
                    {doc.badge && (
                      <span
                        style={{
                          background: `${accent}18`,
                          color: accent,
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          letterSpacing: "0.07em",
                          padding: "0.2em 0.65em",
                          borderRadius: 999,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                          marginTop: 3,
                          flexShrink: 0,
                        }}
                      >
                        {doc.badge}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {doc.description && (
                    <p style={{ color: "#5a6a50", lineHeight: 1.72, fontSize: "0.95rem", flex: 1, marginBottom: "1.5rem" }}>
                      {doc.description}
                    </p>
                  )}

                  {/* CTA */}
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontWeight: 700,
                      fontSize: "0.88rem",
                      color: hasUrl ? accent : "#bbb",
                      marginTop: "auto",
                    }}
                  >
                    {hasUrl ? (
                      <>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            background: `${accent}18`,
                            fontSize: "0.75rem",
                          }}
                        >
                          ↗
                        </span>
                        View Document
                      </>
                    ) : (
                      <>
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: "50%", background: "#eee", fontSize: "0.75rem" }}>
                          ⏳
                        </span>
                        Coming Soon
                      </>
                    )}
                  </div>
                </div>
              );

              return (
                <motion.div key={i} variants={fadeUp} style={{ height: "100%" }}>
                  {hasUrl ? (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "block", height: "100%" }}>
                      {cardContent}
                    </a>
                  ) : (
                    cardContent
                  )}
                </motion.div>
              );
            })}
          </motion.div>

          {/* Footer note */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ color: "#9aaa90", fontSize: "0.88rem", marginTop: "2.5rem", textAlign: "center", lineHeight: 1.7 }}
          >
            These documents are reviewed and updated periodically by the SJFM Board of Directors.
            <br />
            Questions? Reach out to the Market Manager at{" "}
            <a href="mailto:marketmanager@sjfm.ca" style={{ color: "#C19A3B", fontWeight: 600, textDecoration: "none" }}>
              marketmanager@sjfm.ca
            </a>
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default Policies;
